import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {ChartsLabelMark, legendClasses, LineChart} from '@mui/x-charts';
import {XYVO} from "@/service/analyze";
import {useMemo} from "react";
import {OptionType} from "@/model/global";
import {lineElementClasses} from "@mui/x-charts/LineChart";

export interface Recent12mProductProps {
    option: OptionType
    recent12AssignCopyrightTime?: XYVO[]
    recent12AssignPatentTime?: XYVO[]
    recent12AssignProjectTime?: XYVO[]
    recent12AssignTrademarkTime?: XYVO[]
    recent12FinishCopyrightTime?: XYVO[]
    recent12FinishPatentTime?: XYVO[]
    recent12FinishProjectTime?: XYVO[]
    recent12FinishTrademarkTime?: XYVO[]
    recent12ReceiveCopyrightTime?: XYVO[]
    recent12ReceivePatentTime?: XYVO[]
    recent12ReceiveProjectTime?: XYVO[]
    recent12ReceiveTrademarkTime?: XYVO[]
    loading: boolean
}

export default function Recent12mProduct(props: Recent12mProductProps) {
    const {
        loading,
        option, recent12AssignProjectTime, recent12AssignPatentTime, recent12AssignTrademarkTime, recent12FinishPatentTime,
        recent12FinishCopyrightTime, recent12FinishProjectTime, recent12FinishTrademarkTime, recent12ReceiveCopyrightTime,
        recent12ReceivePatentTime, recent12ReceiveProjectTime, recent12ReceiveTrademarkTime, recent12AssignCopyrightTime
    } = props

    const {assignTime, finishTime, receiveTime} = useMemo(() => {
        let assignTime: XYVO[] = []
        let finishTime: XYVO[] = []
        let receiveTime: XYVO[] = []
        switch (option.value) {
            case 1:
                assignTime = recent12AssignProjectTime || []
                receiveTime = recent12ReceiveProjectTime || []
                finishTime = recent12FinishProjectTime || []
                break
            case 2:
                assignTime = recent12AssignPatentTime || []
                receiveTime = recent12ReceivePatentTime || []
                finishTime = recent12FinishPatentTime || []
                break;
            case 3:
                assignTime = recent12AssignTrademarkTime || []
                receiveTime = recent12ReceiveTrademarkTime || []
                finishTime = recent12FinishTrademarkTime || []
                break;
            case 4:
                assignTime = recent12AssignCopyrightTime || []
                receiveTime = recent12ReceiveCopyrightTime || []
                finishTime = recent12FinishCopyrightTime || []
                break
        }
        return {assignTime, finishTime, receiveTime}
    }, [props]);

    return (
        <Card variant="outlined" sx={{width: '100%'}}>
            <CardContent>
                <Stack alignContent={'start'} alignItems={'start'} direction={'row'} justifyContent={'space-between'}>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        最近12个月{option.label}数量
                    </Typography>
                    <Stack alignItems={'start'} direction={'row'} spacing={1}>
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={'#89AC46'}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>分配数量</Typography>
                        </Stack>
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={'#66D2CE'}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>接收数量</Typography>
                        </Stack>
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={'#D98324'}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>完成数量</Typography>
                        </Stack>
                    </Stack>
                </Stack>

                <LineChart
                    height={220}
                    series={[
                        {data: assignTime?.map(item => item.y) || [], label: '分配数量', color: '#89AC46', showMark: false,},
                        {data: receiveTime?.map(item => item.y) || [], label: '领取数量', color: '#66D2CE', showMark: false},
                        {data: finishTime?.map(item => item.y) || [], label: '完成数量', color: '#D98324', showMark: false},
                    ]}
                    loading={loading}
                    localeText={{loading: "加载中"}}
                    hideLegend={true}
                    sx={{
                        [`.${lineElementClasses.root}`]: {
                            strokeWidth: 4,
                        },
                    }}
                    slotProps={{
                        legend: {
                            direction: 'horizontal',
                            position: {
                                vertical: 'top',
                                horizontal: 'end',
                            },
                            sx: {
                                gap: '16px',
                                [`.${legendClasses.mark}`]: {
                                    height: 15,
                                    width: 15,
                                },
                                ['.MuiChartsLegend-series']: {
                                    gap: '8px',
                                },
                            },
                        },
                    }}
                    xAxis={[{scaleType: 'point', data: finishTime?.map(item => item.x), disableTicks: true, disableLine: true}]}
                    yAxis={[{width: 32, disableTicks: true, disableLine: true, tickInterval: (value) => value % 2 == 0}]}
                    margin={{left: 0, right: 0, top: 10, bottom: 0}}
                />
            </CardContent>
        </Card>
    );
}
